import FilterItem from "@app/filter/FilterItem";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@comps/ui/collapsible";
import { Command, CommandItem } from "@comps/ui/command";
import { FormField } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Label } from "@comps/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Switch } from "@comps/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { TreeView } from "@comps/ui/tree-view";
import { AggregationType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY, TYPE_ITEMS_RELATION_CARDINALITY, TYPE_ITEMS_RELATION_FILTER, TYPE_ITEMS_RELATION_KEY, TYPE_ITEMS_RELATION_NAME } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { AND, FieldKey, Filter, INBOUND, OUTBOUND } from "@dto/records";
import { DefaultRelationCardinality, getRelationCardinality, ObjectRelation, RelationCardinality } from "@dto/relation";
import { ReactComponent as CardinalityManyManySVG } from "@img/cardinality-many-many.svg";
import { ReactComponent as CardinalityManyOneSVG } from "@img/cardinality-many-one.svg";
import { ReactComponent as CardinalityOneManySVG } from "@img/cardinality-one-many.svg";
import { ReactComponent as CardinalityOneOneSVG } from "@img/cardinality-one-one.svg";
import { GRAY_300_COLOR } from "@utils/colors";
import { getRelationDirection } from "@utils/model";
import { TW_AddFieldTypeArrow, TW_AddFieldTypeItemSelected, TW_AddFieldTypeScrollArea, TW_AddFieldTypeText, TW_AddFieldTypeTreeView, TW_FieldSection_Label, TW_FieldSection_Label_NoFields, TW_GroupEditorItemAdvancedItem, TW_GroupEditorItemAdvancedLabelGrow, TW_GroupFilterItems, TW_LinkCardinalityDiv, TW_LinkCardinalityIcon, TW_LinkCardinalityIconSelected, TW_LinkConfigurationDiv, TW_LinkNameInput, TW_SharedObjectsCollapsibleTrigger } from "@utils/twClasses";
import { idToKey } from "@utils/types";
import { ChevronDown, ChevronRight, CircleHelp } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translateHtml } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { FieldTypeItemsFormProps } from "./FieldTypeItemsForm";


export const newRelationKey = "new-relation-key";

// Sections listing the fields to link: one open at a time, the current
// workspace first.
type LinkSection = "workspace" | "shared";

export function LinkTypeItemsForm({ field, form, onTypeItemsChange, setError, updateAggregation }: FieldTypeItemsFormProps) {
    const { t } = useTranslation();
    const objects = useModelStore(state => state.objectInfos);
    const objectInfos = useModelStore(state => state.selectedObjectInfos);
    const sharedObjects = useModelStore(state => state.sharedObjectInfos);
    const objectByKeys = useModelStore(state => state.objectByKeys);
    const [selectedLinkItem, setSelectedLinkItem] = useState<FieldKey | undefined>(undefined);
    const [selectedRelationKey, setSelectedRelationKey] = useState<string | undefined>(undefined);
    const [selectedRelationCardinality, setSelectedRelationCardinality] = useState<RelationCardinality>(DefaultRelationCardinality);
    const [relations, setRelations] = useState<ObjectRelation[] | undefined>(undefined);
    const [applyRelationFilter, setApplyRelationFilter] = useState<boolean>(false);
    const [relationFilter, setRelationFilter] = useState<Filter | null>(null);
    const wsColor = useModelStore(state => state.wsColor);
    const outboundRelations = useModelStore(state => state.outboundRelations);
    const inboundRelations = useModelStore(state => state.inboundRelations);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const workspaceByKeys = useModelStore(state => state.workspaceByKeys);
    const [openLinkSection, setOpenLinkSection] = useState<LinkSection | null>("workspace");

    useEffect(() => {
        const relationKey = field.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
        const objectKey = field.typeItems?.get(TYPE_ITEMS_OBJECT_KEY);
        const fieldKey = field.typeItems?.get(TYPE_ITEMS_FIELD_KEY);
        if (relationKey) {
            const relations = getRelations({ objectKey: objectKey, fieldKey: fieldKey });
            const relation = relations.find(relation => relation._key === relationKey);
            if (relation && objectKey && fieldKey) {
                setSelectedRelationKey(relation._key);
                setSelectedLinkItem({ objectKey: objectKey, fieldKey: fieldKey })
                setRelations(relations);
                const direction = getRelationDirection(relation, objectInfos?.object._key);
                if (direction === INBOUND) {
                    setSelectedRelationCardinality(getRelationCardinality(relation.destCard, relation.srcCard));
                } else {
                    setSelectedRelationCardinality(getRelationCardinality(relation.srcCard, relation.destCard));
                }

                let relationFilter: Filter | null = null;
                if (direction === OUTBOUND) {
                    relationFilter = relation.destFilter ?? null;
                } else if (direction === INBOUND) {
                    relationFilter = relation.srcFilter ?? null;
                }
                // Load filter from relation
                if (relationFilter) {
                    setRelationFilter(relationFilter);
                    setApplyRelationFilter(true);
                }
            }
        }
    }, []);

    useEffect(() => {
        if (relations && relations.length > 0) {
            const relationKey = field.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
            let relation = relations[0]
            if (relationKey) {
                relation = relations.find(relation => relation._key === relationKey) || relations[0];
            }
            setSelectedRelationKey(relation._key);
            const direction = getRelationDirection(relation, objectInfos?.object._key);
            let cardinality: RelationCardinality;
            if (direction === INBOUND) {
                cardinality = getRelationCardinality(relation.destCard, relation.srcCard);
            } else {
                cardinality = getRelationCardinality(relation.srcCard, relation.destCard);
            }
            setSelectedRelationCardinality(cardinality);

            let relationFilter: Filter | null = null;
            if (direction === OUTBOUND) {
                relationFilter = relation.destFilter ?? null;
            } else if (direction === INBOUND) {
                relationFilter = relation.srcFilter ?? null;
            }
            // Load filter from relation
            if (relationFilter) {
                setRelationFilter(relationFilter);
                setApplyRelationFilter(true);
            }
            const linkItems = new Map<string, any>(form.getValues("fieldTypeItems"));
            linkItems.set(TYPE_ITEMS_RELATION_NAME, relation.name);
            linkItems.set(TYPE_ITEMS_RELATION_KEY, relation._key);
            linkItems.set(TYPE_ITEMS_RELATION_CARDINALITY, cardinality);
            if (relationFilter) {
                linkItems.set(TYPE_ITEMS_RELATION_FILTER, relationFilter);
            }
            form.setValue("fieldTypeItems", linkItems);
            onTypeItemsChange(linkItems);
        } else {
            setSelectedRelationKey(newRelationKey);
            const linkItems = new Map<string, any>(form.getValues("fieldTypeItems"));
            linkItems.set(TYPE_ITEMS_RELATION_NAME, undefined);
            linkItems.set(TYPE_ITEMS_RELATION_KEY, newRelationKey);
            form.setValue("fieldTypeItems", linkItems);
            onTypeItemsChange(linkItems);
        }
    }, [relations]);

    useEffect(() => {
        var defaultRelationName = "";
        var currentRelationName = form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_RELATION_NAME);
        if (relations?.length === 0 && selectedRelationKey === newRelationKey && (!currentRelationName || currentRelationName === "")) {
            const targetObjectKey = form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_OBJECT_KEY);
            const targetObject = objectByKeys?.get(targetObjectKey);
            defaultRelationName = targetObject?.object.name ?? "";
            const linkItems = new Map<string, any>(form.getValues("fieldTypeItems"));
            linkItems.set(TYPE_ITEMS_RELATION_NAME, defaultRelationName);
            form.setValue("fieldTypeItems", linkItems);
            onTypeItemsChange(linkItems);
        }
    }, [form.getValues("fieldTypeItems"), objectByKeys, selectedRelationKey, relations]);

    function getRelations(fieldKey: FieldKey): ObjectRelation[] {
        let relations: ObjectRelation[] = [];
        if (selectedObjectInfos?.object._key) {
            const outbounds = outboundRelations?.get(selectedObjectInfos.object._key) || [];
            const inbounds = inboundRelations?.get(selectedObjectInfos.object._key) || [];
            if (outbounds && outbounds.length > 0) {
                relations = relations.concat(outbounds.filter(outbound => outbound._to.split("/")[1] === fieldKey.objectKey));
            }
            if (inbounds && inbounds.length > 0) {
                relations = relations.concat(inbounds.filter(inbound => inbound._from.split("/")[1] === fieldKey.objectKey));
            }
            return relations;
        }
        return [];
    }

    function onLinkClicked(fieldKey: FieldKey | undefined) {
        setSelectedLinkItem(fieldKey);
        setSelectedRelationKey(undefined);
        if (fieldKey === undefined) {
            form.setValue("fieldTypeItems", new Map<string, any>());
            onTypeItemsChange(new Map<string, any>());
            return;
        }

        if (!fieldKey?.objectKey || !fieldKey?.fieldKey) {
            console.error("No destination object or field selected");
            return;
        }

        const linkItems = new Map<string, any>();
        linkItems.set(TYPE_ITEMS_OBJECT_KEY, fieldKey.objectKey);
        linkItems.set(TYPE_ITEMS_FIELD_KEY, fieldKey.fieldKey);
        linkItems.set(TYPE_ITEMS_RELATION_KEY, newRelationKey);
        linkItems.set(TYPE_ITEMS_RELATION_NAME, undefined);
        linkItems.set(TYPE_ITEMS_RELATION_CARDINALITY, RelationCardinality.ManyToOne);
        form.setValue("fieldTypeItems", linkItems);
        onTypeItemsChange(linkItems);
        setRelations(getRelations(fieldKey));
    }

    function onNewRelationNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        const relationName = event.target.value;
        const linkItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        linkItems.set(TYPE_ITEMS_RELATION_NAME, relationName);
        form.setValue("fieldTypeItems", linkItems);
        onTypeItemsChange(linkItems);
    }

    function onRelationChange(relationKey: string) {
        const linkItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        setSelectedRelationKey(relationKey);
        if (relationKey === newRelationKey) {
            const cardinality = RelationCardinality.ManyToOne
            setSelectedRelationCardinality(cardinality);
            // Reset filter for new relation
            setRelationFilter(null);
            setApplyRelationFilter(false);
            linkItems.set(TYPE_ITEMS_RELATION_NAME, undefined);
            linkItems.set(TYPE_ITEMS_RELATION_KEY, newRelationKey);
            linkItems.set(TYPE_ITEMS_RELATION_CARDINALITY, cardinality);
            linkItems.delete(TYPE_ITEMS_RELATION_FILTER);
            form.setValue("fieldTypeItems", linkItems);
            onTypeItemsChange(linkItems);

            let aggregation = AggregationType.CONCAT;
            if (cardinality === RelationCardinality.ManyToOne || cardinality === RelationCardinality.OneToOne) {
                aggregation = AggregationType.NO_AGGR;
            }
            updateAggregation(aggregation);
        } else {
            const relation = relations?.find(relation => relation._key === relationKey);
            if (relation) {
                const direction = getRelationDirection(relation, objectInfos?.object._key);
                let cardinality = getRelationCardinality(relation.srcCard, relation.destCard);
                if (direction === INBOUND) {
                    cardinality = getRelationCardinality(relation.destCard, relation.srcCard);
                }

                let relationFilter: Filter | null = null;
                if (direction === OUTBOUND) {
                    relationFilter = relation.destFilter ?? null;
                } else if (direction === INBOUND) {
                    relationFilter = relation.srcFilter ?? null;
                }
                // Load filter from relation
                if (relationFilter) {
                    setRelationFilter(relationFilter);
                    setApplyRelationFilter(true);
                    linkItems.set(TYPE_ITEMS_RELATION_FILTER, relationFilter);
                } else {
                    setRelationFilter(null);
                    setApplyRelationFilter(false);
                    linkItems.delete(TYPE_ITEMS_RELATION_FILTER);
                }
                linkItems.set(TYPE_ITEMS_RELATION_KEY, relation._key);
                linkItems.set(TYPE_ITEMS_RELATION_NAME, relation.name);
                linkItems.set(TYPE_ITEMS_RELATION_CARDINALITY, cardinality);
                form.setValue("fieldTypeItems", linkItems);
                onTypeItemsChange(linkItems);
                setSelectedRelationCardinality(cardinality);
            } else {
                setError(t("field.dialog.error.link.bad.relation.key"));
            }
        }
    }

    function getOnCardinalityChange(cardinality: RelationCardinality): React.MouseEventHandler<SVGSVGElement> {
        return function (e: React.MouseEvent<SVGSVGElement>) {
            e.preventDefault();
            e.stopPropagation();
            const linkItems = new Map<string, any>(form.getValues("fieldTypeItems"));
            linkItems.set(TYPE_ITEMS_RELATION_CARDINALITY, cardinality);

            form.setValue("fieldTypeItems", linkItems);
            onTypeItemsChange(linkItems);
            setSelectedRelationCardinality(cardinality);

            if (selectedRelationKey === newRelationKey) {
                let aggregation = AggregationType.CONCAT;
                if (cardinality === RelationCardinality.ManyToOne || cardinality === RelationCardinality.OneToOne) {
                    aggregation = AggregationType.NO_AGGR;
                }
                updateAggregation(aggregation);
            }

        }
    }

    let srcReltationObjectName = "";
    let destReltationObjectName = "";
    let relation: ObjectRelation | undefined = undefined;
    if (selectedRelationKey !== newRelationKey) {
        relation = relations?.find(relation => relation._key === selectedRelationKey);
        if (relation) {
            const fromObjKey = idToKey(relation._from)
            const toObjKey = idToKey(relation._to)
            srcReltationObjectName = objectByKeys?.get(fromObjKey)?.object.name || "";
            destReltationObjectName = objectByKeys?.get(toObjKey)?.object.name || "";
            if (srcReltationObjectName !== objectInfos?.object.name) {
                srcReltationObjectName = objectByKeys?.get(toObjKey)?.object.name || "";
                destReltationObjectName = objectByKeys?.get(fromObjKey)?.object.name || "";
            }
        }
    } else {
        if (selectedLinkItem?.objectKey) {
            srcReltationObjectName = objectInfos?.object.name || "";
            destReltationObjectName = objectByKeys?.get(selectedLinkItem!.objectKey)?.object.name || "";
        }
    }

    const objectTree = objects?.filter(obj => obj.object._key !== objectInfos?.object._key)?.map(obj => ({
        id: obj.object._key,
        name: obj.object.name,
        children: obj.fields?.map(field => ({
            id: field._key,
            name: field.name,
            onClick: () => onLinkClicked({
                objectKey: obj.object._key,
                fieldKey: field._key
            })
        })),
    }));

    const sharedObjectTree = sharedObjects?.map(ws => ({
        id: ws.wsKey,
        name: workspaceByKeys?.get(ws.wsKey)?.name ?? "",
        children: ws.objectInfos?.map(obj => ({
            id: obj.object._key,
            name: obj.object.name,
            children: obj.fields?.map(field => ({
                id: field._key,
                name: field.name,
                onClick: () => onLinkClicked({
                    objectKey: obj.object._key,
                    fieldKey: field._key
                })
            }))
        })),
    }));

    function onNewRelationNameKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    function onApplyRelationFilterChange(checked: boolean) {
        setApplyRelationFilter(checked);
        const linkItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        if (!checked) {
            linkItems.delete(TYPE_ITEMS_RELATION_FILTER);
        } else {
            // Set the filter (even if empty) to indicate the filter section is active
            linkItems.set(TYPE_ITEMS_RELATION_FILTER, relationFilter ?? { singleFilter: null, groupFilter: { filters: [], combinator: AND } });
        }
        form.setValue("fieldTypeItems", linkItems);
        onTypeItemsChange(linkItems);
    }

    function onRelationFiltersChange(filters: Filter[] | null) {
        if (!filters) {
            return;
        }
        const newRelationFilter: Filter = {
            singleFilter: null,
            groupFilter: {
                filters: filters,
                combinator: AND
            }
        };
        setRelationFilter(newRelationFilter);

        if (applyRelationFilter) {
            const linkItems = new Map<string, any>(form.getValues("fieldTypeItems"));
            linkItems.set(TYPE_ITEMS_RELATION_FILTER, newRelationFilter);
            form.setValue("fieldTypeItems", linkItems);
            onTypeItemsChange(linkItems);
        }
    }

    const destObjectInfos: ObjectInfos | null = selectedLinkItem?.objectKey
        ? objectByKeys?.get(selectedLinkItem.objectKey) ?? null
        : null;

    return (
        <FormField
            control={form.control}
            name="fieldTypeItems"
            render={({ field }) => {
                if (!selectedLinkItem) {
                    return (
                        <div className={TW_LinkConfigurationDiv}>
                            <Collapsible open={openLinkSection === "workspace"} onOpenChange={(open) => setOpenLinkSection(open ? "workspace" : null)} className="w-full">
                                <CollapsibleTrigger className={TW_SharedObjectsCollapsibleTrigger}>
                                    <p className={TW_FieldSection_Label}>{t(`field.link.destination`)}</p>
                                    {openLinkSection === "workspace" ? <ChevronDown className={TW_AddFieldTypeArrow} /> : <ChevronRight className={TW_AddFieldTypeArrow} />}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    {/* The trees scroll themselves, so that their search input stays on top. */}
                                    {objectTree && objectTree.length > 0 && <TreeView searchable data={objectTree} className={TW_AddFieldTypeTreeView + " CompozzTreeView " + TW_AddFieldTypeScrollArea} minLevel={1} />}
                                    {(!objectTree || objectTree.length === 0) && (
                                        <p className={TW_FieldSection_Label_NoFields}>{t(`field.link.no.fields.in.workspace`)}</p>
                                    )}
                                </CollapsibleContent>
                            </Collapsible>
                            {sharedObjects && sharedObjects.length > 0 && (
                                <Collapsible open={openLinkSection === "shared"} onOpenChange={(open) => setOpenLinkSection(open ? "shared" : null)} className="w-full">
                                    <CollapsibleTrigger className={TW_SharedObjectsCollapsibleTrigger}>
                                        <p className={TW_FieldSection_Label}>{t(`field.link.shared.objects`)}</p>
                                        {openLinkSection === "shared" ? <ChevronDown className={TW_AddFieldTypeArrow} /> : <ChevronRight className={TW_AddFieldTypeArrow} />}
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <TreeView searchable data={sharedObjectTree} className={TW_AddFieldTypeTreeView + " CompozzTreeView " + TW_AddFieldTypeScrollArea} minLevel={2} />
                                    </CollapsibleContent>
                                </Collapsible>
                            )}
                        </div>
                    )
                } else {
                    return (
                        <div className={TW_LinkConfigurationDiv}>
                            <p className={TW_FieldSection_Label}>{t(`field.link.destination`)}</p>
                            <Command>
                                <CommandItem
                                    className={TW_AddFieldTypeItemSelected}
                                    onSelect={() => onLinkClicked(undefined)}>
                                    <span className={TW_AddFieldTypeText}>{objectByKeys?.get(selectedLinkItem.objectKey)?.object.name + " > " + (fieldByKeys?.get(selectedLinkItem.fieldKey)?.name === undefined ? t("field.link.invalid.field") : fieldByKeys?.get(selectedLinkItem.fieldKey)?.name)}</span>
                                    <ChevronDown className={TW_AddFieldTypeArrow} />
                                </CommandItem>
                            </Command>
                            <p className={TW_FieldSection_Label}>{t(`field.link.relation.name`)}</p>
                            <Select onValueChange={onRelationChange} value={selectedRelationKey || relations?.[0]?._key || newRelationKey}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {relations && relations.map((relation) => {
                                        return (
                                            <SelectItem key={relation._key} value={relation._key}>{relation.name}</SelectItem>
                                        )
                                    })}
                                    <SelectItem key={newRelationKey} value={newRelationKey}>{t('field.link.new.relation')}</SelectItem>
                                </SelectContent>
                            </Select>
                            {((!selectedRelationKey && !relations?.[0]?._key) || selectedRelationKey === newRelationKey) && (
                                <Input className={TW_LinkNameInput} placeholder={t("field.dialog.link.name.placeholder")} onChange={onNewRelationNameChange} onKeyDown={onNewRelationNameKeyDown} defaultValue={form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_RELATION_NAME) ?? ""} />
                            )}
                            <p className={TW_FieldSection_Label}>{t(`field.link.cardinality`)}</p>
                            <TooltipProvider>
                                <div className={TW_LinkCardinalityDiv}>
                                    <Tooltip delayDuration={100}>
                                        <TooltipTrigger>
                                            <CardinalityManyOneSVG
                                                className={(selectedRelationCardinality === RelationCardinality.ManyToOne ? TW_LinkCardinalityIconSelected : TW_LinkCardinalityIcon)}
                                                color="#999999"
                                                onClick={getOnCardinalityChange(RelationCardinality.ManyToOne)} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {translateHtml(t, `field.link.cardinality.${RelationCardinality.ManyToOne}`, [srcReltationObjectName, destReltationObjectName])}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip delayDuration={100}>
                                        <TooltipTrigger>
                                            <CardinalityOneManySVG
                                                className={(selectedRelationCardinality === RelationCardinality.OneToMany ? TW_LinkCardinalityIconSelected : TW_LinkCardinalityIcon)}
                                                color="#999999"
                                                onClick={getOnCardinalityChange(RelationCardinality.OneToMany)} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {translateHtml(t, `field.link.cardinality.${RelationCardinality.OneToMany}`, [srcReltationObjectName, destReltationObjectName])}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip delayDuration={100}>
                                        <TooltipTrigger>
                                            <CardinalityManyManySVG
                                                className={(selectedRelationCardinality === RelationCardinality.ManyToMany ? TW_LinkCardinalityIconSelected : TW_LinkCardinalityIcon)}
                                                color="#999999"
                                                onClick={getOnCardinalityChange(RelationCardinality.ManyToMany)} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {translateHtml(t, `field.link.cardinality.${RelationCardinality.ManyToMany}`, [srcReltationObjectName, destReltationObjectName])}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip delayDuration={100}>
                                        <TooltipTrigger>
                                            <CardinalityOneOneSVG
                                                className={(selectedRelationCardinality === RelationCardinality.OneToOne ? TW_LinkCardinalityIconSelected : TW_LinkCardinalityIcon)}
                                                color="#999999"
                                                onClick={getOnCardinalityChange(RelationCardinality.OneToOne)} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {translateHtml(t, `field.link.cardinality.${RelationCardinality.OneToOne}`, [srcReltationObjectName, destReltationObjectName])}
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            </TooltipProvider>
                            <div className={TW_GroupEditorItemAdvancedItem}>
                                <div className="flex items-center gap-1 w-full mt-2">
                                    <Label className={TW_FieldSection_Label}>{t("field.link.filter")}</Label>
                                    <TooltipProvider>
                                        <Tooltip delayDuration={100}>
                                            <TooltipTrigger asChild>
                                                <CircleHelp className="w-4 h-4 text-gray-400 cursor-pointer ml-2" />
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-xs">
                                                <p>{t("field.link.filter.tooltip")}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                                <Switch
                                    checked={applyRelationFilter}
                                    onCheckedChange={onApplyRelationFilterChange}
                                    style={{ backgroundColor: applyRelationFilter ? wsColor : GRAY_300_COLOR }}
                                />
                            </div>
                            {applyRelationFilter && destObjectInfos && (
                                <RelationFilterItems
                                    relationFilterItems={relationFilter?.groupFilter?.filters ?? null}
                                    objectInfos={destObjectInfos}
                                    onFiltersChange={onRelationFiltersChange}
                                />
                            )}
                        </div>
                    )
                }
            }}
        />
    )
}

interface RelationFilterItemsProps {
    relationFilterItems: Filter[] | null;
    objectInfos: ObjectInfos;
    onFiltersChange: (filters: Filter[] | null) => void;
}

function RelationFilterItems({ relationFilterItems, objectInfos, onFiltersChange }: RelationFilterItemsProps) {
    const usedFilterPathKeys = relationFilterItems?.map(f => f.groupFilter?.filters?.[0]?.singleFilter?.pathKey).filter(Boolean) as string[] ?? [];

    const onFilterChange = (filter: Filter | null) => {
        if (!filter || !filter.groupFilter?.filters?.length || !filter.groupFilter?.filters[0]?.singleFilter?.pathKey) {
            return;
        }

        const pathKey = filter.groupFilter?.filters[0]?.singleFilter?.pathKey;
        const newFilters = (relationFilterItems || []).map(f => {
            if (f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey === pathKey) {
                return filter;
            }
            return f;
        });
        const newFilter = newFilters.find(f => f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey === pathKey);
        if (!newFilter) {
            newFilters.push(filter);
        }
        onFiltersChange(newFilters);
    };

    const onFilterRemove = (pathKey: string | null) => {
        if (!pathKey) {
            return;
        }

        const newFilters = (relationFilterItems || []).filter(f => {
            return f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey !== pathKey;
        });
        onFiltersChange(newFilters);
    };

    return (
        <div className={TW_GroupFilterItems}>
            {relationFilterItems?.map((filterItem, index) => (
                <FilterItem
                    key={"relation-filter-" + index}
                    filter={filterItem}
                    objectInfos={objectInfos}
                    onFilterChange={onFilterChange}
                    onFilterRemove={onFilterRemove}
                    usedFilterPathKeys={usedFilterPathKeys}
                    disabled={false}
                />
            ))}
            <FilterItem
                key={"relation-filter-" + (relationFilterItems?.length ?? 0)}
                filter={null}
                objectInfos={objectInfos}
                onFilterChange={onFilterChange}
                onFilterRemove={onFilterRemove}
                usedFilterPathKeys={usedFilterPathKeys}
                disabled={false}
            />
        </div>
    );
}